import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";
import { ChevronLeft } from "lucide-react";

// Very small markdown-ish renderer for headings, lists and paragraphs
function renderContent(md = "") {
  const lines = md.split(/\r?\n/);
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${out.length}`}>{listBuf.map((li, i) => <li key={i}>{li}</li>)}</ul>);
      listBuf = [];
    }
  };
  lines.forEach((raw, i) => {
    const l = raw.trim();
    if (!l) { flushList(); return; }
    if (l.startsWith("### ")) { flushList(); out.push(<h3 key={i}>{l.slice(4)}</h3>); return; }
    if (l.startsWith("## "))  { flushList(); out.push(<h3 key={i}>{l.slice(3)}</h3>); return; }
    if (l.startsWith("- ")) { listBuf.push(l.slice(2)); return; }
    flushList();
    out.push(<p key={i}>{l}</p>);
  });
  flushList();
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <div className="text-slate-600">Article not found.</div>
      <Link to="/blog" className="mt-4 inline-block text-[#0A6EBD] font-semibold">← Back to blog</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;

  return (
    <div>
      <article className="py-14 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A6EBD]">
            <ChevronLeft className="h-4 w-4" /> All articles
          </Link>
          <SectionLabel>{post.category}</SectionLabel>
          <h1 className="font-serif text-3xl md:text-5xl tracking-tight text-slate-900 leading-tight">{post.title}</h1>
          {post.excerpt && <p className="mt-4 text-slate-600 text-lg leading-relaxed">{post.excerpt}</p>}
          {post.cover_image && (
            <img src={post.cover_image} alt={post.title} className="mt-8 rounded-2xl w-full aarogya-shadow" />
          )}
          <div className="prose-aarogya mt-8">{renderContent(post.content)}</div>
          <div className="mt-12 rounded-2xl border border-slate-200/70 p-6 bg-[#F1F6FB]">
            <div className="font-serif text-xl text-slate-900">Have a question about this?</div>
            <p className="text-slate-600 text-sm mt-1">Book a private consultation with Dr. Onkar for personal medical advice.</p>
            <Link to="/appointment" className="mt-4 inline-flex rounded-full px-5 py-2.5 text-white text-sm font-semibold"
                  style={{ background: "#0A6EBD" }}>Book Appointment</Link>
          </div>
        </div>
      </article>
    </div>
  );
}
